import { useState } from 'react';
import { Mail, Phone, Instagram, MessageCircle, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import SiteNav from '../components/SiteNav';
import { contactApi } from '../lib/api';

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SiteNav />
      <div className="pt-16">
        <ContactHero />
        <ContactBody />
      </div>
      <ContactFooter />
    </div>
  );
}

function ContactHero() {
  return (
    <div className="bg-[#1C1917] py-20 px-6 md:px-12 text-center">
      <p className="text-[#DDA15E] font-semibold text-sm uppercase tracking-widest mb-4">Get in Touch</p>
      <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
        We'd Love to Hear From You
      </h1>
      <p className="text-white/60 max-w-xl mx-auto text-lg">
        Questions about the programme, in-person classes, or workshops? Reach out and we'll get back to you within 24 hours.
      </p>
    </div>
  );
}

function ContactBody() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', interest: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setFormData(f => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await contactApi.submit(formData);
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or WhatsApp us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-20 px-6 md:px-12 lg:px-24">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-12">

        {/* Contact Info */}
        <div className="lg:col-span-2 space-y-8">
          <div>
            <h2 className="font-heading text-2xl font-bold text-[#292524] mb-6">Contact Details</h2>
            <div className="space-y-5">
              <InfoRow icon={<Mail className="h-5 w-5" />} label="Email" value="connect@thekalarimovement.com" href="mailto:connect@thekalarimovement.com" />
              <InfoRow icon={<Phone className="h-5 w-5" />} label="Phone / WhatsApp" value="+91 80891 43192" href="https://wa.me/918089143192" />
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-[#292524] mb-4 text-sm uppercase tracking-wide">Follow Us</h3>
            <div className="flex gap-3">
              <SocialLink href="https://instagram.com/thekalarimovement" icon={<Instagram className="h-5 w-5" />} label="Instagram" />
              <SocialLink href="https://wa.me/919876543210" icon={<MessageCircle className="h-5 w-5" />} label="WhatsApp" />
            </div>
          </div>

          <div className="bg-[#F4EFE6] rounded-2xl p-6 space-y-3">
            <h3 className="font-heading font-bold text-[#292524]">Upcoming Offerings</h3>
            <OfferingItem label="21-Day Online Programme" status="Oct 1 · Enrolling Now" active />
            <OfferingItem label="In-Person Classes" status="Coming Soon" />
            <OfferingItem label="Workshops & Retreats" status="Coming Soon" />
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-2xl border border-[#E7E5E4] p-8">
            {submitted ? (
              <div className="text-center py-10 space-y-4">
                <div className="w-16 h-16 bg-[#F0FAF5] rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="h-8 w-8 text-[#2C5E4B]" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-[#292524]">Message Received!</h3>
                <p className="text-[#78716C] max-w-sm mx-auto">Thanks {formData.name.split(' ')[0]}, we've got your message and will get back to you within 24 hours.</p>
                <button onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', interest: '', message: '' }); }} className="text-[#9A3B26] text-sm font-medium hover:underline">
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <h2 className="font-heading text-xl font-bold text-[#292524] mb-6">Send Us a Message</h2>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Full Name <span className="text-red-500">*</span></Label>
                    <Input value={formData.name} onChange={set('name')} required placeholder="Your name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email Address <span className="text-red-500">*</span></Label>
                    <Input type="email" value={formData.email} onChange={set('email')} required placeholder="you@example.com" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Phone / WhatsApp</Label>
                    <Input type="tel" value={formData.phone} onChange={set('phone')} placeholder="+91 98765 43210" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>I'm interested in</Label>
                    <select
                      value={formData.interest}
                      onChange={set('interest')}
                      className="w-full rounded-lg border border-[#E7E5E4] px-3 py-2 text-sm bg-white text-[#292524]"
                    >
                      <option value="">Select an option</option>
                      <option>21-Day Online Programme</option>
                      <option>In-Person Classes</option>
                      <option>Workshops & Retreats</option>
                      <option>General Enquiry</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Message <span className="text-red-500">*</span></Label>
                  <Textarea
                    value={formData.message}
                    onChange={set('message')}
                    required rows={5}
                    placeholder="Tell us a bit about yourself and what you're looking for..."
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-500 bg-red-50 rounded-lg px-4 py-3">{error}</p>
                )}

                <Button type="submit" disabled={submitting} className="w-full bg-[#9A3B26] hover:bg-[#7A2E1E] py-3 text-base">
                  <Send className="h-4 w-4 mr-2" />
                  {submitting ? 'Sending…' : 'Send Message'}
                </Button>

                <p className="text-xs text-[#A8A29E] text-center">
                  We typically reply within 24 hours.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon, label, value, href }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 bg-[#F4EFE6] rounded-xl flex items-center justify-center text-[#9A3B26] shrink-0">
        {icon}
      </div>
      <div>
        <p className="text-xs text-[#A8A29E] font-medium uppercase tracking-wide">{label}</p>
        {href ? (
          <a href={href} className="text-[#292524] font-medium hover:text-[#9A3B26] transition-colors">{value}</a>
        ) : (
          <p className="text-[#292524] font-medium">{value}</p>
        )}
      </div>
    </div>
  );
}

function SocialLink({ href, icon, label }) {
  return (
    <a
      href={href} target="_blank" rel="noopener noreferrer"
      className="flex items-center gap-2 bg-[#F4EFE6] hover:bg-[#E7DDD5] text-[#9A3B26] font-medium text-sm px-4 py-2.5 rounded-xl transition-colors"
    >
      {icon} {label}
    </a>
  );
}

function OfferingItem({ label, status, active }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-[#292524] font-medium">{label}</span>
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${active ? 'bg-[#9A3B26] text-white' : 'bg-white text-[#78716C]'}`}>
        {status}
      </span>
    </div>
  );
}

function ContactFooter() {
  return (
    <footer className="bg-[#141210] text-white/50 py-8 px-6 md:px-12 text-center text-sm">
      © {new Date().getFullYear()} The Kalari Movement · <a href="mailto:connect@thekalarimovement.com" className="hover:text-white transition-colors">connect@thekalarimovement.com</a>
    </footer>
  );
}
